(() => {
  class BespokeDiscovery {
    constructor(root) {
      this.root = root;
      this.screens = [...root.querySelectorAll('[data-screen]')];
      this.progress = root.querySelector('[data-progress]');
      this.count = root.querySelector('[data-count]');
      this.actions = root.querySelector('[data-actions]');
      this.message = root.querySelector('[data-message]');
      this.primary = this.actions.querySelector('[data-next]');
      this.defaultNext = this.primary.innerHTML;
      this.config = JSON.parse(root.querySelector('[data-config]').textContent);
      this.key = `bespoke-discovery:${root.dataset.sectionId}`;
      this.state = this.readState();
      this.index = Math.min(Number(this.state._step || 0), Math.max(this.screens.length - 2, 0));
      this.bind();
      this.restore();
      this.show(this.index, false);
    }

    readState() {
      try { return JSON.parse(sessionStorage.getItem(this.key)) || {}; }
      catch (_) { return {}; }
    }

    save() {
      this.state._step = this.index;
      sessionStorage.setItem(this.key, JSON.stringify(this.state));
    }

    bind() {
      this.root.querySelectorAll('[data-next]').forEach(button => button.addEventListener('click', () => this.next()));
      this.root.querySelectorAll('[data-back]').forEach(button => button.addEventListener('click', () => this.back()));
      this.root.querySelectorAll('[data-skip]').forEach(button => button.addEventListener('click', () => this.skip()));

      this.root.addEventListener('input', event => {
        const field = event.target.closest('[data-field]');
        if (!field) return;
        this.state[field.dataset.field] = field.value;
        this.updateConcierge(field.closest('[data-screen]'));
        this.clearMessage();
        this.refresh();
        this.save();
      });

      this.root.addEventListener('click', event => {
        const option = event.target.closest('[data-value]');
        if (!option) return;
        const group = option.closest('[data-choice-group]');
        if (!group) return;
        const key = group.dataset.choiceGroup;
        if (group.dataset.mode === 'single') {
          group.querySelectorAll('[data-value]').forEach(item => item.setAttribute('aria-pressed', String(item === option)));
          this.state[key] = option.dataset.value;
        } else {
          const selected = new Set(Array.isArray(this.state[key]) ? this.state[key] : []);
          if (selected.has(option.dataset.value)) selected.delete(option.dataset.value); else selected.add(option.dataset.value);
          this.state[key] = [...selected];
          option.setAttribute('aria-pressed', String(selected.has(option.dataset.value)));
        }
        this.toggleOther(group.closest('[data-screen]'), key);
        this.toggleAtelier(group.closest('[data-screen]'));
        this.updateChoiceConcierge(group.closest('[data-screen]'), key);
        this.clearMessage();
        this.refresh();
        this.save();
      });

      document.addEventListener('shopify:block:select', event => {
        const screen = event.target.closest('[data-screen]');
        if (screen && this.root.contains(screen)) this.show(this.screens.indexOf(screen), false);
      });
    }

    restore() {
      this.root.querySelectorAll('[data-field]').forEach(field => {
        if (this.state[field.dataset.field] != null) field.value = this.state[field.dataset.field];
      });
      this.root.querySelectorAll('[data-choice-group]').forEach(group => {
        const value = this.state[group.dataset.choiceGroup];
        group.querySelectorAll('[data-value]').forEach(option => {
          const pressed = Array.isArray(value) ? value.includes(option.dataset.value) : value === option.dataset.value;
          option.setAttribute('aria-pressed', String(pressed));
        });
        this.toggleOther(group.closest('[data-screen]'), group.dataset.choiceGroup);
        this.toggleAtelier(group.closest('[data-screen]'));
      });
      this.screens.forEach(screen => {
        this.updateConcierge(screen);
        const group = screen.querySelector('[data-choice-group]');
        if (group) this.updateChoiceConcierge(screen, group.dataset.choiceGroup);
      });
    }

    show(index, persist = true) {
      this.index = Math.max(0, Math.min(index, this.screens.length - 1));
      this.screens.forEach((screen, i) => screen.classList.toggle('is-active', i === this.index));
      const screen = this.screens[this.index];
      const type = screen.dataset.type;
      this.root.classList.toggle('is-cover', type === 'cover');
      this.root.classList.toggle('is-success', type === 'success');
      this.root.classList.toggle('is-dark-step', screen.classList.contains('is-dark'));
      this.root.classList.toggle('is-application-step', type === 'application');
      this.root.classList.toggle('is-review-step', type === 'review');
      this.count.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.screens.length).padStart(2, '0')}`;
      this.progress.style.width = `${this.screens.length > 1 ? (this.index / (this.screens.length - 1)) * 100 : 0}%`;
      if (type === 'review') this.renderReview();
      const navigationEnabled = type !== 'cover' && type !== 'success';
      const showBack = navigationEnabled && screen.dataset.showBack === 'true' && this.index > 0;
      const showSkip = navigationEnabled && type !== 'contact' && screen.dataset.showSkip === 'true';
      const showNext = navigationEnabled && screen.dataset.showNext === 'true';
      const backButton = this.actions.querySelector('[data-back]');
      const skipButton = this.actions.querySelector('[data-skip]');
      backButton.hidden = !showBack;
      skipButton.hidden = !showSkip;
      this.primary.hidden = !showNext;
      backButton.textContent = screen.dataset.backLabel || 'Back';
      skipButton.textContent = screen.dataset.skipLabel || 'Skip';
      this.primary.innerHTML = `${this.escape(screen.dataset.nextLabel || 'Next')} <span aria-hidden="true">&#8594;</span>`;
      this.actions.hidden = !showBack && !showSkip && !showNext;
      const sideBack = this.root.querySelector('.bespoke-discovery__side-nav [data-back]');
      const sideNext = this.root.querySelector('.bespoke-discovery__side-nav [data-next]');
      if (sideBack) sideBack.hidden = !showBack;
      if (sideNext) sideNext.hidden = !showNext;
      this.clearMessage();
      this.refresh();
      screen.scrollTop = 0;
      const activePanel = screen.querySelector('.bespoke-discovery__panel');
      if (activePanel) activePanel.scrollTop = 0;
      if (persist) this.save();
    }

    refresh() {
      const complete = this.isComplete(this.screens[this.index]);
      this.primary.disabled = !complete;
      this.root.querySelectorAll('.bespoke-discovery__side-nav [data-next]').forEach(button => button.disabled = !complete);
    }

    isComplete(screen) {
      if (!screen || screen.dataset.required !== 'true') return true;
      switch (screen.dataset.type) {
        case 'contact': {
          const business = String(this.state.business || '').trim();
          const website = String(this.state.website || '').trim();
          const contact = String(this.state.contact || '').trim();
          const email = String(this.state.email || '').trim();
          const phone = String(this.state.phone || '').trim();
          const accountManager = String(this.state.account_manager || '').trim();
          return Boolean(business && website && contact && phone && accountManager && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        }
        case 'text_fields': {
          const first = screen.querySelector('[data-field]');
          return Boolean(first && String(this.state[first.dataset.field] || '').trim());
        }
        case 'multi_choice': {
          const group = screen.querySelector('[data-choice-group]');
          return Boolean(group && Array.isArray(this.state[group.dataset.choiceGroup]) && this.state[group.dataset.choiceGroup].length);
        }
        case 'atmosphere': return Boolean(String(this.state.atmosphere || '').trim() || this.state.mood);
        case 'application': return Boolean(Array.isArray(this.state.applications) && this.state.applications.length);
        default: return true;
      }
    }

    next() {
      const screen = this.screens[this.index];
      if (!this.isComplete(screen)) { this.showMessage(this.config.requiredMessage); return; }
      if (screen.dataset.type === 'review') { this.submit(); return; }
      if (this.index < this.screens.length - 1) this.show(this.index + 1);
    }

    back() { if (this.index > 0) this.show(this.index - 1); }
    skip() {
      if (this.screens[this.index]?.dataset.type === 'contact') { this.showMessage(this.config.requiredMessage); return; }
      if (this.index < this.screens.length - 1) this.show(this.index + 1);
    }
    showMessage(text) { this.message.textContent = text; this.message.hidden = false; }
    clearMessage() { this.message.hidden = true; this.message.textContent = ''; }

    toggleOther(screen, key) {
      if (!screen) return;
      const selected = this.state[key];
      const show = Array.isArray(selected) && selected.includes('Other');
      const other = screen.querySelector('[data-other]');
      if (other) other.hidden = !show;
    }

    toggleAtelier(screen) {
      const panel = screen && screen.querySelector('[data-atelier]');
      if (!panel) return;
      const triggers = panel.dataset.triggers.split(/\r?\n/).map(value => value.trim()).filter(Boolean);
      panel.hidden = !(this.state.applications || []).some(value => triggers.includes(value));
    }

    updateConcierge(screen) {
      const message = screen && screen.querySelector('[data-concierge]');
      const field = screen && screen.querySelector('[data-field]');
      if (!message || !field) return;
      message.classList.toggle('is-visible', String(this.state[field.dataset.field] || '').trim().length >= Number(message.dataset.minLength || 15));
    }

    updateChoiceConcierge(screen, key) {
      if (!screen) return;
      const message = screen.querySelector('[data-choice-concierge]');
      const moodMessage = screen.querySelector('[data-mood-response]');
      const values = this.state[key] || [];
      const hasValue = Array.isArray(values) ? values.length > 0 : Boolean(values);
      if (message) {
        if (hasValue && key === 'environments') {
          const labels = values.map(value => value === 'Other' && this.value('environments_other') ? this.value('environments_other') : value);
          message.textContent = `Noted — ${this.naturalList(labels)}.\nThat tells us a lot about how the scent should carry.`;
        }
        message.classList.toggle('is-visible', hasValue);
      }
      if (moodMessage && key === 'mood') {
        const options = [...screen.querySelectorAll('[data-choice-group="mood"] [data-value]')].map(option => option.dataset.value);
        const replies = moodMessage.dataset.replies.split(/\r?\n/);
        const selectedIndex = options.indexOf(String(values));
        moodMessage.textContent = selectedIndex >= 0 ? (replies[selectedIndex] || '') : '';
        moodMessage.classList.toggle('is-visible', Boolean(moodMessage.textContent));
      }
    }

    naturalList(values) {
      if (values.length < 2) return values.join('');
      if (values.length === 2) return `${values[0]} and ${values[1]}`;
      return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
    }

    value(key) {
      const value = this.state[key];
      if (Array.isArray(value)) return value.join(', ');
      return String(value || '');
    }

    renderReview() {
      const review = this.root.querySelector('[data-review]');
      if (!review) return;
      const rows = [
        ['Business', [this.value('business'), this.value('website')].filter(Boolean).join(' · ')],
        ['Contact', [this.value('contact'), this.value('email'), this.value('phone')].filter(Boolean).join(' · ')],
        ['Account Manager', this.value('account_manager')],
        ['About', this.value('about')], ['In 3–5 words', this.value('words')],
        ['Created for', this.value('who')], ['Encountered in', this.withOther('environments')],
        ['Should remember', this.value('remember')], ['Atmosphere', this.value('atmosphere')], ['Mood', this.value('mood')],
        ['References', this.value('references')], ['What resonates', this.value('what_resonates')],
        ['Notes drawn to', [this.value('notes_drawn_to'), this.value('notes_custom')].filter(Boolean).join(', ')], ['Notes to avoid', this.value('notes_to_avoid')],
        ['Applications', this.withOther('applications')], ['Future opportunities', this.value('atelier_interest')], ['Intensity', this.value('intensity')],
        ['Final thoughts', this.value('final_thoughts')]
      ];
      review.innerHTML = rows.map(([label, value]) => `<div class="bespoke-discovery__review-row"><span>${this.escape(label)}</span><span>${this.escape(value || '—')}</span></div>`).join('');
    }

    withOther(key) {
      return (this.state[key] || []).map(value => value === 'Other' && this.value(`${key}_other`) ? `Other — ${this.value(`${key}_other`)}` : value).join(', ');
    }

    payload() {
      const data = { platform: 'Bespoke Fragrance Discovery', submitted_at: new Date().toISOString(), shop_domain: window.Shopify && Shopify.shop || location.hostname, section_id: this.root.dataset.sectionId };
      Object.keys(this.state).filter(key => key !== '_step').forEach(key => { data[key] = this.state[key]; });
      data.environments_formatted = this.withOther('environments');
      data.applications_formatted = this.withOther('applications');
      return data;
    }

    async submit() {
      const meaningfulKeys = Object.keys(this.state).filter(key => key !== '_step');
      const hasMeaningfulAnswer = meaningfulKeys.some(key => {
        const value = this.state[key];
        return Array.isArray(value) ? value.length > 0 : String(value || '').trim() !== '';
      });
      if (!hasMeaningfulAnswer) {
        this.showMessage('Please complete at least one field or selection before submitting.');
        return;
      }
      const endpoint = this.root.dataset.endpoint;
      if (!endpoint) { this.showMessage(this.config.submissionError); return; }
      this.primary.disabled = true;
      try {
        const response = await fetch(endpoint, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(this.payload()) });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok === false) throw new Error(result.error || `HTTP ${response.status}`);
        sessionStorage.removeItem(this.key);
        this.show(this.screens.length - 1, false);
      } catch (error) {
        console.error('Bespoke Fragrance Discovery submission:', error);
        this.showMessage(this.config.submissionError);
        this.primary.disabled = false;
      }
    }

    escape(value) { const div = document.createElement('div'); div.textContent = value; return div.innerHTML; }
  }

  const init = scope => scope.querySelectorAll('[data-bespoke-discovery]').forEach(root => { if (!root.bespokeDiscovery) root.bespokeDiscovery = new BespokeDiscovery(root); });
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();



